/**
 * Decides whether an iOS visitor should be invited to use the native app.
 *
 * Kept pure and separate from the component so the rules — which device
 * counts as iOS, how long a dismissal lasts, when we must stay out of the way
 * — can be pinned down in tests without a DOM.
 */

export const DISMISS_DAYS = 1;

/**
 * The iOS app is Bloomberg's. Consignment has no app to install, so offering
 * one there would be an advert for something that doesn't exist — that holds
 * for dev_consignment too. Plain dev is in the list so the prompt can be
 * checked on a real iPhone before it reaches Bloomberg contributors; the app
 * it links to is the live App Store one either way.
 *
 * Sourced from REACT_APP_POSTHOG_ENV because that is the one variable the
 * pipeline already sets per deployment (see .gitlab-ci.yml — dev,
 * prod_bloomberg, dev_consignment, prod_consignment). Add new deployments
 * here as they get their own app.
 */
export const DEPLOYMENTS_WITH_IOS_APP = ["prod_bloomberg", "dev"];

export const deploymentHasIosApp = (environment) =>
  DEPLOYMENTS_WITH_IOS_APP.includes(environment);

/**
 * iPadOS 13+ reports itself as "Macintosh". Touch points are the tell: a real
 * Mac reports 0, an iPad reports 5. Without this an iPad user never sees the
 * prompt, which is the device the app arguably helps most.
 */
export const isIosUserAgent = (userAgent = "", maxTouchPoints = 0) => {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
};

export const isDismissalCurrent = (dismissedAt, now = Date.now()) => {
  const timestamp = Number(dismissedAt);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return false;

  // A clock that has moved backwards (timezone fix, restored backup) would
  // otherwise make the dismissal look infinitely far in the future.
  const elapsed = now - timestamp;
  if (elapsed < 0) return true;

  return elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export const shouldOfferIosApp = ({
  userAgent = "",
  maxTouchPoints = 0,
  dismissedAt = null,
  uploadsInFlight = 0,
  environment = process.env.REACT_APP_POSTHOG_ENV,
  now = Date.now(),
} = {}) => {
  if (!deploymentHasIosApp(environment)) return false;
  if (!isIosUserAgent(userAgent, maxTouchPoints)) return false;
  if (isDismissalCurrent(dismissedAt, now)) return false;

  // Never talk over a running transfer. The prompt's whole argument is that
  // the app uploads more reliably; interrupting an upload to say so would be
  // both rude and risky.
  if (uploadsInFlight > 0) return false;

  return true;
};
